import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
	ArrowUpRight,
	X,
	Plus,
	Loader2,
	ShoppingCart,
	FileText,
	PackageCheck,
	FileSignature,
	Link2,
	Send,
	Archive,
	ArchiveRestore,
	Trash2,
	ChevronDown,
	ChevronRight,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { formatarBRL } from "../../helpers/dinheiro";
import useLarguraArrastavel from "../../hooks/useLarguraArrastavel";
import usePermissions from "../../hooks/usePermissions";
import { copiarTexto } from "../../helpers/copiarTexto";
import useModules from "../../hooks/useModules";
import PageTabs from "../PageTabs";
import SituacaoBadge from "../SituacaoBadge";
import OrcamentoDoCartao from "../OrcamentoDoCartao";
import LinhaDoTempoDaVenda from "../LinhaDoTempoDaVenda";

// Proposta e contrato do contato, só para leitura aqui: editar é na tela de
// Propostas. Estados dos dois tipos no mesmo mapa — o painel não separa.
const SITUACAO_DA_PROPOSTA = {
	draft: { rotulo: "Rascunho", classe: "text-muted-foreground" },
	sent: { rotulo: "Enviada", classe: "text-primary" },
	viewed: { rotulo: "Aberta pelo cliente", classe: "text-sky-600" },
	accepted: { rotulo: "Aceita", classe: "text-emerald-600" },
	rejected: { rotulo: "Recusada", classe: "text-destructive" },
	expired: { rotulo: "Vencida", classe: "text-muted-foreground" },
	pending_signature: { rotulo: "Esperando assinatura", classe: "text-primary" },
	signed: { rotulo: "Assinada", classe: "text-emerald-600" },
	cancelled: { rotulo: "Cancelada", classe: "text-destructive" },
};

// O painel de vendas da conversa: abas Orçamento / Pedido / Proposta, tudo do
// contato deste atendimento. O orçamento se monta aqui e o PDF sai pela
// própria conversa — com o `ticketId`, para ficar registrado nela.
//
// Não há entidade nova: o vínculo com a conversa é o contato (listagem) mais
// o ticket no envio. É irmão do ContactDrawer no Ticket, mesma posição e mesmo
// comportamento em tela estreita (camada por cima abaixo de 1280px).
const CarrinhoDaConversa = ({ open, onClose, contact, ticket }) => {
	const { temPermissao } = usePermissions();
	const { temModulo } = useModules();
	const podeEditar = temPermissao("quotes:manage");
	const comPropostas = temModulo("propostas");

	const [orcamentos, setOrcamentos] = useState([]);
	// O que aconteceu DEPOIS do aceite. Quem atende precisa responder "já
	// pagou?", "já saiu?" sem abrir outra tela — é a pergunta que o cliente faz
	// na própria conversa.
	const [pedidos, setPedidos] = useState({});
	const [pedidosLista, setPedidosLista] = useState([]);
	const [propostas, setPropostas] = useState([]);
	const [abertoId, setAbertoId] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [criando, setCriando] = useState(false);
	const [criandoCarrinho, setCriandoCarrinho] = useState(false);
	// Arquivados ficam fora da lista por padrão — é o que impede o cliente
	// antigo de virar um rolo de orçamentos velhos. O bloco abre sob demanda.
	const [arquivados, setArquivados] = useState([]);
	const [totalArquivados, setTotalArquivados] = useState(0);
	const [mostrarArquivados, setMostrarArquivados] = useState(false);
	const [ocupadoPedido, setOcupadoPedido] = useState(null);
	const [aba, setAba] = useState("orcamento");
	// A aba default segue o contexto UMA vez por conversa: depois que a pessoa
	// clicou, a escolha dela manda.
	const abaDefinidaPara = useRef(null);

	const contactId = contact?.id;
	const ehGrupo = !!ticket?.isGroup;

	const {
		ref: refDoPainel,
		largura: larguraDoPainel,
		ativo: larguraVale,
		iniciarArrasto,
	} = useLarguraArrastavel({
		chave: "larguraPainelCarrinho",
		padrao: 360,
		min: 300,
		max: 560,
		invertido: true,
		aPartirDe: 1280,
	});

	const carregar = useCallback(async () => {
		if (!contactId || ehGrupo) return;

		setCarregando(true);
		try {
			const { data } = await api.get("/quotes", { params: { contactId } });
			const quotes = data.quotes || [];
			setOrcamentos(quotes);
			setTotalArquivados(data.totais?.arquivados || 0);

			// Empresa sem o pedido ligado nunca tem linha aqui: volta vazio e
			// nenhum chip aparece.
			const { data: vendas } = await api
				.get("/orders", { params: { contactId } })
				.catch(() => ({ data: { orders: [] } }));

			const orders = vendas.orders || [];
			setPedidosLista(orders);
			setPedidos(
				Object.fromEntries(
					orders.filter((p) => p.quoteId).map((p) => [p.quoteId, p]),
				),
			);

			let proposals = [];
			if (comPropostas) {
				const { data: docs } = await api
					.get("/proposals", { params: { contactId } })
					.catch(() => ({ data: { proposals: [] } }));
				proposals = docs.proposals || [];
				setPropostas(proposals);
			}

			// A aba default responde "o que está acontecendo com este cliente
			// AGORA": pedido andando > orçamento esperando resposta > proposta em
			// aberto > montar orçamento.
			if (abaDefinidaPara.current !== contactId) {
				abaDefinidaPara.current = contactId;

				const pedidoAberto = orders.some((p) =>
					["pending_payment", "paid", "separating", "shipped"].includes(
						p.status,
					),
				);
				const orcamentoAberto = quotes.some((q) =>
					["sent", "viewed"].includes(q.status),
				);
				const propostaAberta = proposals.some((p) =>
					["sent", "viewed", "pending_signature"].includes(p.status),
				);

				setAba(
					pedidoAberto
						? "pedido"
						: orcamentoAberto
							? "orcamento"
							: propostaAberta
								? "proposta"
								: "orcamento",
				);
			}
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [contactId, ehGrupo, comPropostas]);

	useEffect(() => {
		if (open) carregar();
	}, [open, carregar]);

	// Os arquivados só carregam quando o bloco abre — e recarregam quando a
	// lista ativa muda (arquivar/desarquivar mexe nas duas).
	useEffect(() => {
		if (!mostrarArquivados || !contactId) {
			return;
		}

		api
			.get("/quotes", { params: { contactId, status: "archived" } })
			.then(({ data }) => setArquivados(data.quotes || []))
			.catch(toastError);
	}, [mostrarArquivados, contactId, orcamentos]);

	// Esc fecha — é camada, não navegação.
	useEffect(() => {
		if (!open) return undefined;

		const aoTeclar = (e) => {
			if (e.key === "Escape") onClose?.();
		};

		document.addEventListener("keydown", aoTeclar);
		return () => document.removeEventListener("keydown", aoTeclar);
	}, [open, onClose]);

	// "Aceito pelo link público" acontece no celular do cliente, longe desta
	// tela: sem escutar o evento da empresa, o chip continuaria dizendo
	// "enviado" até alguém apertar F5.
	useEffect(() => {
		if (!open || !contactId) return undefined;

		const socket = openSocket();

		socket.on("quote", (data) => {
			if (Number(data.quote?.contactId) !== Number(contactId)) return;
			carregar();
		});

		// O pedido anda em outra tela (quem separa não é quem atende): sem isto
		// o chip diria "a pagar" depois de o dinheiro ter entrado.
		socket.on("order", () => carregar());

		return () => socket.disconnect();
	}, [open, contactId, carregar]);

	// O catálogo que o cliente monta sozinho: nasce como orçamento em rascunho
	// marcado como carrinho, e o que vai para a conversa é o LINK — não um PDF.
	// Quem escolhe os itens é o cliente, e o preço vem do banco no momento em
	// que ele escolhe.
	const mandarCatalogo = async () => {
		setCriandoCarrinho(true);

		try {
			const { data } = await api.post("/quotes", { contactId, isCart: true });

			if (!data?.publicToken) {
				toast.error(
					"O carrinho foi criado, mas ficou sem link. Abra em Orçamentos para copiar."
				);
				setCriandoCarrinho(false);
				carregar();
				return;
			}

			const link = `${window.location.origin}/carrinho/${data.publicToken}`;

			if (ticket?.id) {
				await api.post(`/messages/${ticket.id}`, {
					body: `Monte seu pedido por aqui: ${link}`,
					fromMe: true,
				});
				toast.success("Link do catálogo enviado na conversa.");
			} else {
				// Pelo helper: a recusa da área de transferência (aba fora de
				// foco) caía no catch de fora, que engolia o link do carrinho
				// recém-criado num toastError genérico e ainda pulava a recarga
				// da lista.
				await copiarTexto(link, "Link do catálogo copiado.");
			}

			carregar();
		} catch (err) {
			toastError(err);
		}

		setCriandoCarrinho(false);
	};

	const criar = async () => {
		setCriando(true);
		try {
			const { data } = await api.post("/quotes", { contactId, source: "conversa" });
			setOrcamentos((atuais) => [data, ...atuais]);
			setAbertoId(data.id);
		} catch (err) {
			toastError(err);
		}
		setCriando(false);
	};

	// Cobrar daqui é mandar o link NA CONVERSA: o backend reaproveita a
	// cobrança existente e reenvia o mesmo link — nunca cobra duas vezes.
	const cobrarNaConversa = async (pedido) => {
		setOcupadoPedido(pedido.id);
		try {
			await api.post(`/orders/${pedido.id}/charge`, {
				enviarNaConversa: true,
			});
			toast.success("Link de pagamento enviado na conversa.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupadoPedido(null);
	};

	const arquivar = async (orcamento, valor) => {
		try {
			await api.put(`/quotes/${orcamento.id}/arquivar`, { arquivar: valor });
			toast.success(
				valor ? "Orçamento arquivado." : "Orçamento devolvido à lista.",
			);
			if (abertoId === orcamento.id) setAbertoId(null);
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const apagarRascunho = async (orcamento) => {
		if (
			!window.confirm(
				`Excluir o orçamento nº ${orcamento.number}? Só rascunho pode ser apagado — o que já foi enviado se arquiva.`,
			)
		) {
			return;
		}

		try {
			await api.delete(`/quotes/${orcamento.id}`);
			toast.success("Rascunho excluído.");
			if (abertoId === orcamento.id) setAbertoId(null);
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const copiarLinkDoPedido = (pedido) => copiarTexto(pedido.chargeUrl);

	// Pedido e Proposta só entram quando existem para a empresa — nunca aba
	// que a loja jamais preenche. Contador é o total do contato.
	const abas = [
		{
			value: "orcamento",
			rotulo: "Orçamento",
			icone: FileText,
			contador: orcamentos.length,
		},
		{
			value: "pedido",
			rotulo: "Pedido",
			icone: PackageCheck,
			contador: pedidosLista.length,
		},
		...(comPropostas || propostas.length
			? [
					{
						value: "proposta",
						rotulo: "Proposta",
						icone: FileSignature,
						contador: propostas.length,
					},
				]
			: []),
	];

	return (
		<>
			{/* Abaixo de xl o painel é camada, e o véu diz isso: escurece a
			    conversa atrás e fecha no toque. */}
			{open && (
				<div
					aria-hidden="true"
					onClick={onClose}
					className="fixed inset-0 z-20 bg-black/40 xl:hidden"
				/>
			)}
		<div
			ref={refDoPainel}
			style={open && larguraVale ? { width: larguraDoPainel } : undefined}
			className={cn(
				"relative h-full overflow-hidden transition-[width] duration-200 ease-in-out",
				// Camada que desliza da direita abaixo de xl, com sombra — em vez
				// de aparecer seca por cima da conversa.
				// Camada de tela cheia abaixo de xl. `fixed`, e não `absolute`: como
				// `absolute`, o painel fechado não saía da tela com o
				// `translate-x-full` — ficava 126px para dentro, cobrindo a conversa,
				// cortado na borda e inerte (pointer-events: none), sem que nada na
				// tela explicasse aquilo. Preso à viewport, o fechado sai inteiro e o
				// aberto ocupa a tela.
				// Camada de tela cheia abaixo de xl, presa à VIEWPORT.
				//
				// Fechado, ele simplesmente não existe no celular (`max-xl:hidden`),
				// em vez de sair de cena por `translate`. O deslizar era bonito e não
				// funcionava: com `position: absolute` o painel fechado parava 126px
				// dentro da tela, cortado e inerte, e mesmo preso à viewport a classe
				// de aberto não vencia a de fechado — aberto e fechado pintavam no
				// mesmo lugar, fora da tela. Sem transformação não há o que empatar.
				"max-xl:fixed max-xl:inset-y-0 max-xl:right-0 max-xl:z-30 max-xl:w-full max-xl:max-w-[420px] max-xl:shadow-xl",
				open
					? "w-[360px] shrink-0"
					: "w-0 shrink-0 max-xl:hidden",
			)}
		>
			{open && (
				<div
					role="separator"
					aria-orientation="vertical"
					aria-label="Arraste para mudar a largura do painel de vendas"
					title="Arraste para mudar a largura do painel de vendas"
					onPointerDown={iniciarArrasto}
					className="absolute inset-y-0 left-0 z-10 hidden w-1.5 cursor-col-resize touch-none transition-colors hover:bg-primary/30 active:bg-primary/40 xl:block"
				/>
			)}

			<div className="flex h-full w-full flex-col border-l bg-background max-xl:w-full">
				<div className="flex min-h-[57px] shrink-0 items-center gap-2 border-b bg-muted/40 px-2">
					<Button variant="ghost" size="icon" onClick={onClose} aria-label="Fechar painel de vendas">
						<X className="h-5 w-5" />
					</Button>
					<span className="text-sm font-medium">Vendas</span>
				</div>

				{!ehGrupo && contactId && (
					<PageTabs
						abas={abas}
						atual={aba}
						onChange={setAba}
						className="shrink-0 bg-muted/40"
					/>
				)}

				<div className="flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4">
					{ehGrupo ? (
						<p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
							Orçamento é para uma pessoa, e este atendimento é de grupo. Abra a
							conversa do contato que vai comprar.
						</p>
					) : !contactId ? (
						<p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
							Este atendimento ainda não tem contato carregado.
						</p>
					) : aba === "orcamento" ? (
						<>
							{/* A história inteira em duas linhas, no vocabulário único:
							    orçamento nasce rascunho, é enviado, aceito, vira pedido.
							    "Carrinho" não existe mais em texto de tela. */}
							<p className="rounded-lg bg-muted/60 p-2 text-[11px] leading-relaxed text-muted-foreground">
								A venda começa como <strong>orçamento</strong>: monte, envie e
								espere o aceite. Aceito, vira <strong>pedido</strong> — é lá
								que se cobra, separa e entrega. Rascunho não aparece em
								Pedidos, e não reserva estoque.
							</p>

							{podeEditar ? (
								<>
								<Button
									size="sm"
									variant="outline"
									className="w-full"
									onClick={criar}
									disabled={criando}
								>
									{criando ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<Plus className="h-4 w-4" />
									)}
									Novo orçamento
								</Button>
								{/* O outro caminho: em vez de montar aqui, mandar o
								    catálogo e deixar o cliente escolher. Volta como
								    orçamento enviado, na mesma lista abaixo. */}
								<Button
									size="sm"
									variant="outline"
									className="w-full"
									onClick={mandarCatalogo}
									disabled={criandoCarrinho}
								>
									{criandoCarrinho ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<ShoppingCart className="h-4 w-4" />
									)}
									Mandar catálogo para o cliente montar
								</Button>
								</>
							) : (
								// Sem permissão ≠ sem função: dizer por que o botão não
								// está aqui poupa o "a tela quebrou?" para o gestor.
								<p className="rounded-lg border border-dashed p-2 text-center text-[11px] text-muted-foreground">
									Você pode ver os orçamentos, mas não criar ou editar —
									fale com seu gestor.
								</p>
							)}

							{carregando && !orcamentos.length && (
								<div className="flex justify-center py-6">
									<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
								</div>
							)}

							{!carregando && !orcamentos.length && (
								<div className="space-y-1 rounded-lg border border-dashed p-4 text-center">
									<ShoppingCart className="mx-auto h-5 w-5 text-muted-foreground" />
									<p className="text-xs text-muted-foreground">
										Nenhum orçamento para este contato. Monte um aqui e mande o
										PDF pela própria conversa.
									</p>
								</div>
							)}

							{orcamentos.map((orcamento) => {
								const aberto = abertoId === orcamento.id;

								return (
									<div key={orcamento.id} className="space-y-2">
										{/* Ações fora do botão de expandir: botão dentro de
										    botão é HTML inválido e um clique engole o outro. */}
										<div className="flex items-stretch gap-1">
											<button
												type="button"
												onClick={() =>
													setAbertoId(aberto ? null : orcamento.id)
												}
												aria-expanded={aberto}
												className="min-w-0 flex-1 space-y-1.5 rounded-lg border bg-background px-3 py-2 text-left hover:bg-muted"
											>
												<span className="flex items-center gap-2">
													<span className="min-w-0 flex-1 truncate text-sm font-medium">
														Orçamento nº {orcamento.number}
													</span>
													<span className="shrink-0 text-sm tabular-nums">
														{formatarBRL(orcamento.total)}
													</span>
												</span>
												{/* Fechado, a linha do tempo conta a história
												    inteira; aberto, quem conta é o cartão. */}
												{!aberto && (
													<>
														<LinhaDoTempoDaVenda
															compacto
															situacao={orcamento.status}
															pedido={pedidos[orcamento.id]}
														/>
														{/* "Por que perdemos" sem expandir: é a
														    resposta que quem reabre procura. */}
														{orcamento.status === "rejected" &&
															orcamento.rejectionReason && (
																<span className="block truncate text-[11px] text-destructive">
																	Motivo: {orcamento.rejectionReason}
																</span>
															)}
														{orcamento.envioFalhou && (
															<span className="block text-[11px] text-destructive">
																PDF não entregue no canal — reenvie.
															</span>
														)}
													</>
												)}
											</button>

											{podeEditar && (
												<div className="flex flex-col justify-center">
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 text-muted-foreground"
														title="Arquivar orçamento"
														aria-label={`Arquivar o orçamento nº ${orcamento.number}`}
														onClick={() => arquivar(orcamento, true)}
													>
														<Archive className="h-3.5 w-3.5" />
													</Button>
													{orcamento.status === "draft" && (
														<Button
															variant="ghost"
															size="icon"
															className="h-7 w-7 text-muted-foreground hover:text-destructive"
															title="Excluir rascunho"
															aria-label={`Excluir o orçamento nº ${orcamento.number}`}
															onClick={() => apagarRascunho(orcamento)}
														>
															<Trash2 className="h-3.5 w-3.5" />
														</Button>
													)}
												</div>
											)}
										</div>

										{aberto && (
											<OrcamentoDoCartao
												linhaCompacta
												quoteId={orcamento.id}
												contactId={contactId}
												ticketId={ticket?.id}
												onMudou={carregar}
											/>
										)}
									</div>
								);
							})}

							{/* O histórico não some: quem quiser reencontra. */}
							{totalArquivados > 0 && (
								<div className="space-y-2 pt-1">
									<button
										type="button"
										onClick={() => setMostrarArquivados((v) => !v)}
										className="flex w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
									>
										{mostrarArquivados ? (
											<ChevronDown className="h-3.5 w-3.5" />
										) : (
											<ChevronRight className="h-3.5 w-3.5" />
										)}
										Arquivados ({totalArquivados})
									</button>

									{mostrarArquivados &&
										arquivados.map((orcamento) => (
											<div
												key={orcamento.id}
												className="flex items-center gap-2 rounded-lg border border-dashed bg-background/60 px-3 py-2 opacity-75"
											>
												<span className="min-w-0 flex-1 truncate text-sm">
													Orçamento nº {orcamento.number}
												</span>
												<span className="shrink-0 text-sm tabular-nums">
													{formatarBRL(orcamento.total)}
												</span>
												{podeEditar && (
													<Button
														variant="ghost"
														size="icon"
														className="h-7 w-7 shrink-0 text-muted-foreground"
														title="Devolver à lista"
														aria-label={`Desarquivar o orçamento nº ${orcamento.number}`}
														onClick={() => arquivar(orcamento, false)}
													>
														<ArchiveRestore className="h-3.5 w-3.5" />
													</Button>
												)}
											</div>
										))}
								</div>
							)}
						</>
					) : aba === "pedido" ? (
						<>
							{/* Aqui se lê e se cobra; separar e despachar é trabalho de
							    quem está no estoque, na tela Pedidos. */}
							<p className="rounded-lg bg-muted/60 p-2 text-[11px] leading-relaxed text-muted-foreground">
								Acompanhe e cobre daqui. Separar, despachar e entregar é na
								tela <strong>Pedidos</strong>.
							</p>

							{!carregando && !pedidosLista.length && (
								<div className="space-y-1 rounded-lg border border-dashed p-4 text-center">
									<PackageCheck className="mx-auto h-5 w-5 text-muted-foreground" />
									<p className="text-xs text-muted-foreground">
										Nenhum pedido deste contato. Ele nasce sozinho quando o
										cliente aceita um orçamento.
									</p>
								</div>
							)}

							{pedidosLista.map((pedido) => (
								<div
									key={pedido.id}
									className="space-y-1.5 rounded-lg border bg-background px-3 py-2"
								>
									<span className="flex items-center gap-2">
										<span className="min-w-0 flex-1 truncate text-sm font-medium">
											Venda nº {pedido.quote?.number ?? pedido.number}
										</span>
										<span className="shrink-0 text-sm tabular-nums">
											{formatarBRL(pedido.total)}
										</span>
									</span>
									<LinhaDoTempoDaVenda compacto pedido={pedido} />
									{pedido.quoteId && (
										<Link
											to={`/vendas/${pedido.quoteId}`}
											className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
										>
											Abrir venda
											<ArrowUpRight className="h-3.5 w-3.5" />
										</Link>
									)}
									{(pedido.chargeUrl ||
										(podeEditar && pedido.status === "pending_payment")) && (
										<div className="flex flex-wrap gap-1.5 pt-0.5">
											{pedido.chargeUrl && (
												<Button
													size="sm"
													variant="outline"
													className="h-7 text-xs"
													onClick={() => copiarLinkDoPedido(pedido)}
												>
													<Link2 className="h-3.5 w-3.5" />
													Copiar link
												</Button>
											)}
											{podeEditar &&
												(pedido.status === "pending_payment" ||
													pedido.chargeUrl) && (
													<Button
														size="sm"
														variant="outline"
														className="h-7 text-xs"
														disabled={ocupadoPedido === pedido.id}
														onClick={() => cobrarNaConversa(pedido)}
													>
														{ocupadoPedido === pedido.id ? (
															<Loader2 className="h-3.5 w-3.5 animate-spin" />
														) : (
															<Send className="h-3.5 w-3.5" />
														)}
														{pedido.chargeUrl
															? "Reenviar link na conversa"
															: "Cobrar na conversa"}
													</Button>
												)}
										</div>
									)}
								</div>
							))}
						</>
					) : (
						<>
							{/* Só leitura: proposta se edita na tela de Propostas. */}
							<p className="rounded-lg bg-muted/60 p-2 text-[11px] leading-relaxed text-muted-foreground">
								As propostas e contratos deste contato. Para editar ou enviar,
								use a tela <strong>Propostas</strong> — ou gere uma a partir de
								um orçamento na aba ao lado.
							</p>

							{!carregando && !propostas.length && (
								<div className="space-y-1 rounded-lg border border-dashed p-4 text-center">
									<FileSignature className="mx-auto h-5 w-5 text-muted-foreground" />
									<p className="text-xs text-muted-foreground">
										Nenhuma proposta para este contato. Gere uma pelo botão
										"Gerar proposta" de um orçamento.
									</p>
								</div>
							)}

							{propostas.map((proposta) => (
								<div
									key={proposta.id}
									className="space-y-1 rounded-lg border bg-background px-3 py-2"
								>
									<span className="flex items-center gap-2">
										<span className="min-w-0 flex-1 truncate text-sm font-medium">
											{proposta.title || `Proposta nº ${proposta.number}`}
										</span>
										<span className="shrink-0 text-sm tabular-nums">
											{formatarBRL(proposta.totalValue)}
										</span>
									</span>
									<SituacaoBadge
										situacao={
											SITUACAO_DA_PROPOSTA[proposta.status] ||
											SITUACAO_DA_PROPOSTA.draft
										}
									/>
								</div>
							))}
						</>
					)}
				</div>
			</div>
		</div>
		</>
	);
};

export default CarrinhoDaConversa;
